import React, { lazy, Suspense, useEffect, useState } from "react";
import { Route, Routes, useLocation, useParams } from "react-router-dom";

import { Sidebar, MobileSidebar, MobileSidebarProvider } from "./Components/layout";
import ProtectedRoute from "./Components/ProtectedRoute/ProtectedRoute";
import CognitoProtectedRoute from "./Components/CognitoProtectedRoute/CognitoProtectedRoute";
import { Provider } from "./context/DataContext";
import { PageSectionSkeleton } from "./Components/ui/spinner";
import { installBugReportDiagnostics } from "./Components/BugReport/bugReportDiagnostics";
import lazyWithPreload from "./utils/lazyWithPreload";
import ErrorBoundary from "./Components/ErrorBoundary";

// Overlays load after the first paint, in their own chunks (antd, MUI, html2canvas, the
// assistant...), instead of in the main bundle.
const DraggableFloatingButtonPopup = lazy(() => import("./Components/DraggableFloatingButtonPopup"));
const BugReportOverlay = lazy(() => import("./Components/BugReport"));
const PortalAssistantOverlay = lazy(() => import("./Components/PortalAssistant"));

const Login = lazy(() => import("./Pages/Login"));
const Signup = lazy(() => import("./Pages/Signup"));
const TreatmentViewDisplay = lazy(() => import("./Pages/TreatmentViewDisplay"));
const TestingPage = lazy(() => import("./Pages/TestingPage"));
const TodayViewNew = lazy(() => import("./Pages/TodayViewNew"));
const RoomView = lazy(() => import("./Pages/RoomView"));
const Updations = lazy(() => import("./Components/Updations"));
const AuditView = lazy(() => import("./Components/AuditView"));
const FrontDeskContainer = lazy(() => import("./Pages/FrontDesk/FrontDeskContainer"));
const ExportData = lazy(() => import("./Pages/ExportData"));
const LabTracker = lazy(() =>
  import("./Pages/LabTracker").then((module) => ({ default: module.LabTracker }))
);
const AddPatientForm = lazy(() => import("./Pages/Add Patient"));
const PatientSummaryView = lazy(() => import("./Pages/PatientSummary"));
const PageNotFound = lazy(() => import("./Pages/PageNotFound"));
const FiltersView = lazy(() => import("./Pages/Filters/Info"));
const TreatmentView = lazy(() => import("./Pages/Treatmentview_FAST"));
const TodayViewV2 = lazyWithPreload(() => import("./Pages/TodayViewV2"));
const EMR = lazy(() => import('./Pages/EMR'));
const EMRv2 = lazyWithPreload(() => import('./Pages/EMRv2'));
const AddPatientFormNew = lazy(() => import("./Pages/AddPatientFormNew"));
const PreviewFormModal = lazy(() => import("./Pages/AddPatientFormNew/Components/PreviewFormModal"));
const PublicDocumentsUpload = lazy(() => import("./Pages/PublicDocumentsUpload"));
const SharedDocuments = lazy(() => import("./Pages/SharedDocuments"));
const DocumentsView = lazy(() => import("./Pages/DocumentsView"));
const ReviewForm = lazy(() => import("./Pages/ReviewForm"));
const CognitoAuth = lazy(() => import("./Pages/CognitoAuth"));
const CognitoUsers = lazy(() => import("./Pages/CognitoUsers"));
const MFASettings = lazy(() => import("./Pages/MFASettings"));
const CRM = lazy(() => import("./Pages/CRM"));
const TaskManagementPage = lazy(() => import("./Pages/TaskManagement"));
const PatientFormsPage = lazy(() => import("./Pages/PatientForms"));
const DocumentIntelligenceCenter = lazy(() => import("./Pages/DocumentIntelligenceCenter"));
const DailyClinicCommandCenter = lazy(() => import("./Pages/DailyClinicCommandCenter"));
const AdminLogs = lazy(() => import("./Pages/AdminLogs"));

// Main pages whose code a signed-in user's first load starts downloading right away,
// alongside the session check, instead of after it. Nothing renders before the check.
const PRELOADED_PAGES = {
  "/": TodayViewV2,
  "/todayview": TodayViewV2,
  "/emr": EMRv2,
};

/** Starts downloading the boot page's code, then the other main page's once idle. */
export const preloadMainPages = (pathname, { hasSession, whenIdle }) => {
  if (!hasSession) return;
  const current = PRELOADED_PAGES[pathname];
  current?.preload();
  whenIdle(() => {
    [TodayViewV2, EMRv2].forEach((page) => {
      if (page !== current) page.preload();
    });
  });
};

const runWhenIdle = (callback) => {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(callback, { timeout: 5000 });
  } else {
    setTimeout(callback, 2000);
  }
};

// The sidebar opens the overlays with window events. An event sent before an overlay's
// code has loaded is held and sent again once the overlay is listening.
export const createOpenEventBuffer = (eventName) => {
  let ready = false;
  let pendingEvent = null;
  const hold = (event) => {
    if (!ready) pendingEvent = event;
  };
  if (typeof window !== "undefined") window.addEventListener(eventName, hold);
  return {
    markReady() {
      if (ready) return;
      ready = true;
      window.removeEventListener(eventName, hold);
      if (pendingEvent) {
        const { detail } = pendingEvent;
        pendingEvent = null;
        window.dispatchEvent(new CustomEvent(eventName, { detail }));
      }
    },
  };
};

const bugReportOpenEvents = createOpenEventBuffer("sunridge:open-bug-report");
const portalAssistantOpenEvents = createOpenEventBuffer("sunridge:open-portal-assistant");

// Rendered after an overlay in the same Suspense boundary, so its effect runs once the
// overlay's own listeners are registered.
const OverlayReady = ({ events }) => {
  useEffect(() => {
    events.markReady();
  }, [events]);
  return null;
};

const PageFallback = () => (
  <div className="p-3 sm:p-4">
    <PageSectionSkeleton rows={5} />
  </div>
);

const RoutesWrapper = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const location = useLocation();

  // Callback to be passed to Sidebar
  const handleSidebarToggle = (isCollapsed) => {
    setIsSidebarCollapsed(isCollapsed);
  };

  const validRoutes = [
    "/treatmentviewdisplay",
    "/testingpage",
    "/roomview",
    "/updations",
    "/auditview",
    "/labtracker",
    "/frontdesk",
    "/exportdata",
    "/patientform",
    "/reviewform",
    "/medicineinfo",
    "/patientsummary",
    "/filters",
    "/",
    "/treatmentview",
    "/todayview",
    "/todayviewold",
    "/emr",
    "/old-emr",
    "/patientform",
    "/crm",
    "/task-management",
    "/patient-forms",
    "/document-intelligence",
    "/daily-command-center",
    "/admin-logs",
  ];

  const dynamicRoutes = [
    /^\/treatmentview\/\w+$/,
    /^\/auditview\/\w+\/\w+$/,
    /^\/filters\/\w+$/,
    /^\/treatmentviewdisplay\/\w*$/,
    /^\/crm\/.*/,
  ];

  const showNavbar =
    validRoutes.includes(location.pathname) ||
    dynamicRoutes.some((pattern) => pattern.test(location.pathname));
    
  // Determine the class for the content container based on the sidebar visibility
  const contentContainerClass = showNavbar
    ? `transition-all duration-300 ease-in-out ${
        isSidebarCollapsed
          ? "lg:ml-20"
          : "lg:ml-72"
      }`
    : "";

  const { name, formId } = useParams();

  // Start the boot page's code download in parallel with the session check.
  useEffect(() => {
    preloadMainPages(location.pathname, {
      hasSession: Boolean(localStorage.getItem("sessionToken")),
      whenIdle: runWhenIdle,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The bug reporter's console/network capture starts when the navbar shows, as it did
  // when the overlay was part of the main bundle, not when its code arrives.
  useEffect(() => {
    if (showNavbar) installBugReportDiagnostics();
  }, [showNavbar]);

  return (
    <MobileSidebarProvider>
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar - hidden on mobile */}
      {showNavbar && (
        <div className="hidden lg:block">
          <Sidebar onToggle={handleSidebarToggle} />
        </div>
      )}

      {/* Mobile Sidebar - hidden on desktop */}
      {showNavbar && (
        <MobileSidebar className="lg:hidden" />
      )}

      {/* Chats Menu Hidden */}
      {/* {location.pathname !== "/login" && <Chats />} */}
      {(location.pathname !== "/login" && (location.pathname !== "/patientform" && name && formId)) && (
        <ErrorBoundary
          fallback={null}
          chunkErrorMessage="The floating tools button couldn't be loaded (the app may have been updated, or the connection dropped). Reload the page to get it back."
        >
          <Suspense fallback={null}>
            <DraggableFloatingButtonPopup />
          </Suspense>
        </ErrorBoundary>
      )}

      <main className={contentContainerClass}>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />

          {/* Cognito Authentication Routes */}
          <Route path="/cognito-auth" element={<CognitoAuth />} />
          <Route element={<CognitoProtectedRoute />}>
            <Route path="/cognito-users" element={<CognitoUsers />} />
            <Route path="/mfa-settings" element={<MFASettings />} />
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route
              path="/treatmentviewdisplay/:patientId2?"
              element={
                <Provider>
                  <TreatmentViewDisplay />
                </Provider>
              }
            />
            <Route
              path="/treatmentview/:patientId"
              element={
                <Provider>
                  <TreatmentView />
                </Provider>
              }
            />
            <Route
              path="/filters/:patientId"
              element={
                <Provider>
                  <FiltersView />
                </Provider>
              }
            />

            <Route path="/testingpage" element={<TestingPage />} />
            <Route path="/treatmentview" element={<TreatmentView />} />
            <Route path="/roomview" element={<RoomView />} />
            <Route
              path="/updations"
              element={
                <Provider>
                  <Updations />
                </Provider>
              }
            />
            <Route path="/auditview" element={<AuditView />} />
            <Route
              path="/auditview/:patientId/:orderId"
              element={<AuditView />}
            />
            <Route
              path="/labtracker"
              element={
                <Provider>
                  <LabTracker />
                </Provider>
              }
            />
              <Route
                path="/frontdesk"
                element={
                  <Provider>
                    <FrontDeskContainer />
                  </Provider>
                }
              />
            <Route path="/exportdata" element={<ExportData />} />
            <Route path="/patientformold" element={<AddPatientForm />} />
            <Route
              path="/filters"
              element={
                <Provider>
                  <FiltersView />
                </Provider>
              }
            />
            <Route path="/patientsummary" element={<PatientSummaryView />} />
         
            <Route
              path="/old-emr"
              element={
                <>
                <Provider>
                <EMR />
                </Provider>
                </>
              }
            />

            <Route
              path="/emr"
              element={
                <Provider>
                  <EMRv2 />
                </Provider>
              }
            />

            <Route
              path="/patient-forms"
              element={<PatientFormsPage />}
            />

            <Route
              path="/todayviewold"
              element={
                <div className="todayview-bg">
                  <Provider>
                  <TodayViewNew />
                  </Provider>
                </div>
              }
            />
            <Route
              path="/todayview"
              element={
                <Provider>
                  <TodayViewV2 />
                </Provider>
              }
            />
            <Route
              path="/"
              element={
                <Provider>
                  <TodayViewV2 />
                </Provider>
              }
            />
            <Route path="/task-management" element={<TaskManagementPage />} />
            <Route path="/document-intelligence" element={<DocumentIntelligenceCenter />} />
            <Route path="/daily-command-center" element={<DailyClinicCommandCenter />} />
            <Route path="/admin-logs" element={<AdminLogs />} />
            <Route path="/crm/*" element={<CRM />} />

            <Route
              path="/patientform/preview/:name?/:formId?"
              element={
                <AddPatientFormNew />
              }
            />
          </Route>

          {/* Public patient form — accessible without login (link sent via email) */}
          <Route
            path="/patientform/:name/:formId"
            element={
              <PreviewFormModal />
            }
          />

          <Route
            path="/publicdocumentsupload/:patientId"
            element={
              <PublicDocumentsUpload />
            }
          />

          <Route path="/shared-documents/:token" element={<SharedDocuments />} />

          <Route
            path="/documents/:patientname/:patientid"
            element={
              <DocumentsView />
            }
          />

          <Route
            path="/reviewform/:patientId"
            element={
              <ReviewForm />
            }
          />

          <Route path="*" element={<PageNotFound />} />
        </Routes>
        </Suspense>
      </main>
      {showNavbar && (
        <ErrorBoundary
          fallback={null}
          chunkErrorOpenEvent="sunridge:open-bug-report"
          chunkErrorMessage="The bug report couldn't be loaded (the app may have been updated, or the connection dropped). Reload the page to report a bug."
        >
          <Suspense fallback={null}>
            <BugReportOverlay />
            <OverlayReady events={bugReportOpenEvents} />
          </Suspense>
        </ErrorBoundary>
      )}
      {showNavbar && (
        <ErrorBoundary
          fallback={null}
          chunkErrorOpenEvent="sunridge:open-portal-assistant"
          chunkErrorMessage="The AI Assistant couldn't be loaded (the app may have been updated, or the connection dropped). Reload the page to use it."
        >
          <Suspense fallback={null}>
            <PortalAssistantOverlay />
            <OverlayReady events={portalAssistantOpenEvents} />
          </Suspense>
        </ErrorBoundary>
      )}
    </div>
    </MobileSidebarProvider>
  );
};

export default RoutesWrapper;
